import React, { createContext, useContext, useEffect, useState } from "react";
import { AppData, ExpextItem, FaqData, StepItem, CreatingProgramV2Step } from "../../types/AppData";
import { useFirebaseToken } from "../firebase/FirebaseProvider";
import Loader from "../../components/shared/loader";
import { HOST } from "../setting";
import { FIRST_SCREEN_STATIC } from "./firstScreenStatic";
import { useLocation } from "react-router-dom";
import { preloadImage, preloadDuringIdle } from "../../utils/preloader";


interface AppBenefitItem {
    text: string
    success: boolean
}
interface GetWithLola {
    text: string
}

interface AppBenefit {
    app: AppBenefitItem
    tutor: AppBenefitItem
}

interface ExpectDataType {
    [key: string]: ExpextItem;
}

interface AgeData {
    rangeString: string
    image: {
        "male": string,
        "female": string
    }
    startImage: string
}


type DataContextValue = {
    data: AppData | null;
    loading: boolean;
    error: string | null;
    levels: StepItem[];
    goals: StepItem[];
    program: StepItem[];
    expect: ExpectDataType;
    photoFrame: StepItem[];
    ages: AgeData[];
    faq: FaqData[];
    appBenefits: AppBenefit[];
    getwithlola: GetWithLola[];
    creatingProgramV2Steps: CreatingProgramV2Step[];
};

const DataContext = createContext<DataContextValue>({
    data: null,
    loading: true,
    error: null,
    levels: [],
    goals: [],
    program: [],
    expect: {},
    photoFrame: [],
    ages: [],
    faq: [],
    appBenefits: [],
    getwithlola: [],
    creatingProgramV2Steps: []
});

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token, loading: authLoading } = useFirebaseToken();
    const location = useLocation();

    const [data, setData] = useState<AppData | null>({
        static: FIRST_SCREEN_STATIC,
        languages: [],
        hows: [],
        whys: [],
        topics: [],
        test_words: {
            beginner: [],
            intermediate: [],
            advanced: []
        },
        struggles: [],
        reviews: [],
        onboarding_video: {},
        creating_program_v2_steps: [],
        lex_test: {
            questions: []
        },
        summary_keys: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const levels: StepItem[] = [
        { id: 1, title: "beginner", image: "https://cdn-eu.lolaenglish.com/web-images%2FBeginner.svg", localized: {} },
        { id: 2, title: "intermediate", image: "https://cdn-eu.lolaenglish.com/web-images%2FIntermediate.svg", localized: {} },
        { id: 3, title: "advanced", image: "https://cdn-eu.lolaenglish.com/web-images%2FAdvansed.svg", localized: {} },
    ]
    const goals: StepItem[] = [
        { id: 4, title: "60", image: "/Images/60.webp", localized: {} },
        { id: 3, title: "30", image: "https://cdn-eu.lolaenglish.com/web-images%2F30.webp", localized: {} },
        { id: 2, title: "15", image: "https://cdn-eu.lolaenglish.com/web-images%2F15.webp", localized: {} },
    ]
    const program: StepItem[] = [
        { id: 1, title: "web.select.lessons.by.interest", image: "https://cdn-eu.lolaenglish.com/web-images%2Fselect.svg", localized: {} },
        { id: 2, title: "web.adjust.difficulty.level", image: "https://cdn-eu.lolaenglish.com/web-images%2Flevel.svg", localized: {} },
        { id: 3, title: "web.find.relevant.words", image: "https://cdn-eu.lolaenglish.com/web-images%2Ffinding.svg", localized: {} },
        { id: 4, title: "web.add.video.explanations", image: "https://cdn-eu.lolaenglish.com/web-images%2Fvideo.svg", localized: {} },
        { id: 5, title: "web.create.ai.chats", image: "https://cdn-eu.lolaenglish.com/web-images%2Fcreating.svg", localized: {} },
    ]
    const expect: ExpectDataType = {
        "expect1Week": { id: 1, time_learn: "onboarding.estimated.results.header.week", duration: 1, title: "onboarding.estimated.results.learn.to.meet", image: "https://cdn-eu.lolaenglish.com/web-images%2Fweek1.webp", color: "#52A4FF", count_word: 120, count_title: "common.plural.new.words.without.digits", localized: {} },
        "expect4Week": { id: 2, time_learn: "onboarding.estimated.results.header.week", duration: 4, title: "onboarding.estimated.results.learn.to.order", image: "https://cdn-eu.lolaenglish.com/web-images%2Fweek4.webp", color: "#46FFAA", count_word: 24, count_title: "common.plural.new.phrases.without.digits", localized: {} },
        "expect12Week": { id: 3, time_learn: "onboarding.estimated.results.header.week", duration: 12, title: "onboarding.estimated.results.learn.to.talk", image: "https://cdn-eu.lolaenglish.com/web-images%2Fweek12.webp", color: "#F389FF", count_word: 360, count_title: "common.plural.minutes.of.speaking.without.digits", localized: {} },
        "expectMonth12": { id: 4, time_learn: "onboarding.estimated.results.header.month", duration: 12, title: "onboarding.estimated.results.learn.to.speak", image: "https://cdn-eu.lolaenglish.com/web-images%2Fmonth12.webp", localized: {} }
    }
    const photoFrame: StepItem[] = [
        { id: 1, title: "web.practice.real.situations", image: "https://cdn-eu.lolaenglish.com/web-images%2Fsituations.webp", localized: {} },
         { id: 10, title: "web.chat.with.ai", image: "https://cdn-eu.lolaenglish.com/web-images%2Fai.webp", localized: {} },
        // { id: 2, title: "web.speak.vocabulary", image: "https://cdn-eu.lolaenglish.com/web-images%2Fvocabulary.webp", localized: {} },
        { id: 3, title: "web.improve.pronunciation", image: "https://cdn-eu.lolaenglish.com/web-images%2Fpronunciation.webp", localized: {} },
        { id: 4, title: "web.watch.videos", image: "https://cdn-eu.lolaenglish.com/web-images%2Fwatch.webp", localized: {} },
        { id: 5, title: "web.train.fluency", image: "https://cdn-eu.lolaenglish.com/web-images%2Ffluency.webp", localized: {} },
        { id: 6, title: "web.hero.in.series", image: "https://cdn-eu.lolaenglish.com/web-images%2Fhero.webp", localized: {} },
        { id: 7, title: "web.grow.vocabulary", image: "https://cdn-eu.lolaenglish.com/web-images%2Fvocab.webp", localized: {} },
        { id: 8, title: "web.pick.new.words", image: "https://cdn-eu.lolaenglish.com/web-images%2FpickUp.webp", localized: {} },
        { id: 9, title: "web.remember.words.forever", image: "https://cdn-eu.lolaenglish.com/web-images%2Fremember.webp", localized: {} },
        // { id: 11, title: "web.practice.real.situations", image: "https://cdn-eu.lolaenglish.com/web-images%2Fpractice.webp", localized: {} },
    ]

    const ages: AgeData[] = [
        {
            rangeString: "18 - 24",
            image: {
                male: "https://cdn-eu.lolaenglish.com/web-images%2FMale18-24.webp",
                female: "https://cdn-eu.lolaenglish.com/web-images%2FFemale18-24.webp"
            },
            startImage: "https://cdn-eu.lolaenglish.com/web-images%2F18_24.webp"
        },
        { 
            rangeString: "25 - 34", 
            image: {
                male: "https://cdn-eu.lolaenglish.com/web-images%2FMale25-34.webp",
                female: "https://cdn-eu.lolaenglish.com/web-images%2FFemale25-34.webp"
            },
            startImage: "https://cdn-eu.lolaenglish.com/web-images%2F25_34.webp"
        },
        { 
            rangeString: "35 - 44",
            image: {
                male: "https://cdn-eu.lolaenglish.com/web-images%2FMale35-44.webp",
                female: "https://cdn-eu.lolaenglish.com/web-images%2FFemale35-44.webp"
            },
            startImage: "https://cdn-eu.lolaenglish.com/web-images%2F35_44.webp"
        },
        { 
            rangeString: "45+",
            image: {
                male: "https://cdn-eu.lolaenglish.com/web-images%2FMale45.webp",
                female: "https://cdn-eu.lolaenglish.com/web-images%2FFemale45.webp"
            },
            startImage: "https://cdn-eu.lolaenglish.com/web-images%2F45.webp"
        },
    ]
    const faq: FaqData[] = [
        { id: 1, title: "web.faq.how.learn.langarena", description: "web.faq.learn.langarena.explainer", localized: {} },
        { id: 2, title: "web.faq.how.access.langarena", description: "web.faq.access.langarena.instructions", localized: {} },
        { id: 3, title: "web.faq.cancel.subscription", description: "web.faq.cancel.instructions", localized: {} },
    ]

    const appBenefits: AppBenefit[] = [
        { app: { text: "web.instant.feedback", success: true }, tutor: { text: "web.instant.feedback", success: true } },
        { app: { text: "web.practice.anytime", success: true }, tutor: { text: "web.schedule.advance", success: false } },
        { app: { text: "web.understand.accents", success: true }, tutor: { text: "web.little.variety", success: false } },
        { app: { text: "web.stress.free", success: true }, tutor: { text: "web.habit.difficult.break", success: false } },
        { app: { text: "web.fiftyx.cheaper", success: true }, tutor: { text: "web.expensive", success: false } },

    ]

    const getwithlola: GetWithLola[] = [
        { text: "web.improve.all.toefl.skills" },
        { text: "web.realtime.feedback.ai" },
        { text: "web.dynamic.program.adjustments" },
        { text: "web.practice.exercises.2500" },
        { text: "web.results.3x.faster" },
    ]

    const creatingProgramV2Steps: CreatingProgramV2Step[] = [
        {
            id: 1,
            titleKey: "web.analyzing.level",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_1.webp",
            progressPercentage: 16,
            animation: "prog_step_1"
        },
        {
            id: 2,
            titleKey: "web.creating.dialogues",
            questionKey: "web.read.write.better.speak",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_2.webp",
            questionImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_2_q.webp",
            progressPercentage: 32,
            animation: "prog_step_2"
        },
        {
            id: 3,
            titleKey: "web.adding.ai.speaking.practice",
            questionKey: "web.understand.freeze.speak",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_3.webp",
            questionImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_3_q.webp",
            progressPercentage: 48,
            animation: 'prog_step_3'
        },
        {
            id: 4,
            titleKey: "web.select.vocabulary.level",
            questionKey: "web.cannot.remember.words",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_4.webp",
            questionImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_4_q.webp",
            progressPercentage: 64,
            animation: 'prog_step_4'
        },
        {
            id: 5,
            titleKey: "web.pronunciation.training.setup",
            questionKey: "web.sound.more.natural",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_5.webp",
            questionImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_5_q.webp",
            progressPercentage: 80,
            animation: 'prog_step_5'
        },
        {
            id: 6,
            titleKey: "web.mapping.learning.path",
            questionKey: "web.feel.no.progress",
            headerImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_6.webp",
            questionImage: "https://cdn-eu.lolaenglish.com/web-images%2Fprog_step_6_q.webp",
            progressPercentage: 100,
            animation: 'prog_step_6'
        }
    ]

    async function preloadAllImages(freshData: AppData | null) {
        const _data = freshData || data;
        const tasks: Array<() => Promise<void>> = [];
        const addTask = (url: string | null | undefined) => {
            if (!url) return;
            tasks.push(() => preloadImage(url));
        };

        addTask("https://cdn-eu.lolaenglish.com/web-images%2Fhands.webp");
        addTask("https://cdn-eu.lolaenglish.com/web-images%2Fimg-plan.webp");
        addTask("https://cdn-eu.lolaenglish.com/web-images%2FLamp.webp");
        addTask("https://cdn-eu.lolaenglish.com/web-images%2FBrains.webp");
        addTask("https://cdn-eu.lolaenglish.com/web-images%2F2x.webp");
        for (let i in levels) {
            addTask(levels[i].image);
        }
        for (let i in goals) {
            addTask(goals[i].image);
        }
        for (let i in program) {
            addTask(program[i].image);
        }
        for (let i in expect) {
            addTask(expect[i].image);
        }
        for (let i in photoFrame) {
            addTask(photoFrame[i].image);
        }
        for (let i in ages) {
            addTask(ages[i].image.male);
            addTask(ages[i].image.female);
        }
        for (let i in creatingProgramV2Steps) {
            addTask(creatingProgramV2Steps[i].headerImage);
            addTask(creatingProgramV2Steps[i].questionImage);
        }
        if (!_data) {
            if (tasks.length > 0) preloadDuringIdle(tasks);
            return
        }
        for (let i in _data.hows) {
            addTask(_data.hows[i].image)
        }
        for (let i in _data.languages) {
            addTask(_data.languages[i].icon)
        }
        for (let i in _data.struggles) {
            addTask(_data.struggles[i].image)
            addTask(_data.struggles[i].image_content)
        }
        for (let i in _data.topics) {
            addTask(_data.topics[i].image)
        }
        for (let i in _data.whys) {
            addTask(_data.whys[i].image)
        }
        if (_data.grammar_test?.questions) {
            for (let i in _data.grammar_test.questions) {
                const question = _data.grammar_test.questions[i]
                if (question.options) {
                    for (let j in question.options) {
                        addTask(question.options[j].emoji)
                    }
                }
            }
        }
        if (_data.lex_test?.questions) {
            for (let i in _data.lex_test.questions) {
                const question = _data.lex_test.questions[i]
                addTask(question.image)
                if (question.images) {
                    for (let j in question.images) {
                        addTask(question.images[j])
                    }
                }
                if (question.options) {
                    for (let j in question.options) {
                        addTask(question.options[j].image)
                    }
                }
            }
        }

        if (tasks.length > 0) {
            preloadDuringIdle(tasks);
        }
    }


    function applyEnglishLocalization(data: AppData, enId: Number): void {
        const localize = (item: any) => {
            if (item.localized) {
                item.localized[`${enId}`] = item.title;
            }
            if (item.description_localized) {
                item.description_localized[`${enId}`] = item.description
            }
        };

        const arraysToLocalize = [
            data.hows,
            data.whys,
            data.topics,
            data.struggles,
            data.reviews,
            data.test_words.beginner,
            data.test_words.intermediate,
            data.test_words.advanced
        ];

        for (const array of arraysToLocalize) {
            if (!array) {
                continue
            }
            for (const item of array) {
                localize(item);
            }
        }

    }

    useEffect(() => {
        if (authLoading) return;

        const fetchData = async () => {
            try {
                // const res = await fetch(`${HOST}/api/v1/web-onboarding`, {
                //     credentials: "include",
                //     headers: {
                //         "Authorization": `Bearer ${token}`,
                //     },
                // });
                const res = await fetch(`/api/v1/data.json`, {
                    credentials: "include",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (!res.ok) {
                    throw new Error(`Server responded with ${res.status}`);
                }

                const json: AppData = await res.json();

                applyEnglishLocalization(json, json.languages.find(x => x.iso === "en")!.id)

                const lang_iso = navigator.language.split('-')[0]
                const lang_idx = json?.languages.findIndex(x => x.iso.toLocaleLowerCase() === lang_iso.toLocaleLowerCase())
                if (lang_idx > 0) {
                    const [item] = json.languages.splice(lang_idx, 1)
                    json.languages.unshift(item)
                }
                json.languages.push({ id: -1, iso: "en", name: "Other", icon: json.languages.find(x => x.iso === "en")?.icon })
                setData(json);

                // Aggressively preload all images during idle time
                if (typeof requestIdleCallback !== 'undefined') {
                    requestIdleCallback(() => preloadAllImages(json), { timeout: 5000 });
                } else {
                    setTimeout(() => preloadAllImages(json), 1000);
                }
            } catch (err: any) {
                setError(err.message || "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [token, authLoading]);

    if (error) {
        return <div>{error}</div>
    }

    const shouldShowLoader = loading && location.pathname !== "/start" && location.pathname !== "/";

    return (
        <DataContext.Provider value={{ data, loading, error, levels, goals, program, expect, photoFrame, ages, faq, appBenefits, getwithlola, creatingProgramV2Steps }}>
            {shouldShowLoader ? (
                <Loader />
            ) : (
                children
            )}

        </DataContext.Provider>
    );
};

export const useData = () => useContext(DataContext);